
/*!

=========================================================
* Black Dashboard React v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/black-dashboard-react
* Copyright 2020 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/black-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useRef, useCallback } from "react";
// nodejs library that concatenates classes
import classNames from "classnames";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
// reactstrap components
import {
  Button,
  Collapse,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Input,
  NavbarBrand,
  Navbar,
  NavLink,
  Nav,
  NavItem,
  Container,
  Modal,
  NavbarToggler,
  ModalHeader,
} from "reactstrap";
import { Link } from "react-router-dom";
import logo from "../../assets/img/ziro.png";

// AdminNavbar.js

// TODO CLEANUP: Remove deprecated config4js.js imports (ZAI-68)
// config4js.js is deprecated in favor of service_config.js
// Use window.getOAuthClientId() and window.getBackendUrl() instead
// See: https://tadepalli.atlassian.net/browse/ZAI-68
// import { globalClientId } from '../../config4js.js';
// import { serverUrl } from '../../config4js.js';

// Use service_config.js instead (loaded globally via index.html)
const globalClientId = window.getOAuthClientId();
const serverUrl = window.getBackendUrl();

// ID_TOKEN DEBUG_LOG_START - Cookie Renewal Investigation
// Set to false to disable all debug logging
const DEBUG_COOKIE_RENEWAL = true;

// JWT Debug Utilities
const debugDecodeJwt = (token, label = "JWT") => {
  if (!DEBUG_COOKIE_RENEWAL) return null;
  if (!token) {
    console.log(`🔍 [${label}] Token is null or empty`);
    return null;
  }
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.log(`🔍 [${label}] Invalid JWT format (expected 3 parts, got ${parts.length})`);
      return null;
    }
    
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const now = Math.floor(Date.now() / 1000);
    const exp = payload.exp;
    const iat = payload.iat;
    const timeUntilExpiry = exp - now;
    const minutesUntilExpiry = Math.floor(timeUntilExpiry / 60);
    const isExpired = now > exp;
    
    console.log(`🔍 [${label}] JWT Details:`);
    console.log(`   📧 Email: ${payload.email || 'N/A'}`);
    console.log(`   👤 User ID (sub): ${payload.sub || 'N/A'}`);
    console.log(`   📅 Issued At (iat): ${new Date(iat * 1000).toLocaleString()}`);
    console.log(`   ⏰ Expires At (exp): ${new Date(exp * 1000).toLocaleString()}`);
    console.log(`   ⏳ Time Until Expiry: ${minutesUntilExpiry} minutes (${timeUntilExpiry} seconds)`);
    console.log(`   ${isExpired ? '❌ EXPIRED' : '✅ VALID'}`);
    
    return { payload, exp, iat, isExpired, timeUntilExpiry, minutesUntilExpiry };
  } catch (e) {
    console.error(`🔍 [${label}] Error decoding JWT:`, e);
    return null;
  }
};

const debugGetCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const debugShowCurrentCookie = () => {
  if (!DEBUG_COOKIE_RENEWAL) return null;
  
  console.log("🍪 CURRENT COOKIE STATE:");
  const token = debugGetCookie('ziroatoken');
  
  if (!token) {
    console.log("   ❌ No ziroatoken cookie found!");
    return null;
  }
  
  console.log(`   ✅ Cookie found (length: ${token.length} chars)`);
  const jwtInfo = debugDecodeJwt(token, "Cookie JWT");
  return jwtInfo;
};
// ID_TOKEN DEBUG_LOG_END

function AdminNavbar(props) {
  const [collapseOpen, setcollapseOpen] = React.useState(false);
  const [modalSearch, setmodalSearch] = React.useState(false);
  const [color, setcolor] = React.useState("navbar-transparent");
  const [loggedIn, setLoggedIn] = React.useState(false);
  const [userImgURL, setUserURL] = React.useState(() => {
    // Don't initialize from localStorage - wait for auth check
    // This prevents showing avatar when not authenticated
    return null;
  });
  const [userObj, setUserObj] = React.useState(null);
  const [timer, setTimer] = React.useState(null);
  const [tokenClient, setTokenClient] = React.useState(null);
  const [accessToken, setAccessToken] = React.useState(null);
  const [showAutoRenewBanner, setShowAutoRenewBanner] = React.useState(false);
  const [isRequestingRefreshToken, setIsRequestingRefreshToken] = React.useState(false);
  const [autoRenewSuccess, setAutoRenewSuccess] = React.useState(false);
  const history = useHistory();
  
  // Note: BroadcastChannel for cross-tab token refresh has been removed
  // With server-side session management, token refresh is no longer client-side
  // Sessions are managed server-side and don't require cross-tab synchronization

  // Helper function to set authentication cookie with proper flags for localhost/production
  // NOTE: No domain attribute is set - this scopes cookies to the exact hostname
  // (dev.ziro.io, tvd.ziro.io, ziro.io) allowing independent login sessions per subdomain
  const setAuthCookie = React.useCallback((token, expires = null) => {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    let cookieString = `ziroatoken=${token};path=/`;
    
    // Only add Secure flag if not on localhost (Secure requires HTTPS)
    if (!isLocalhost) {
      cookieString += ';Secure';
    }
    
    // SameSite=None requires Secure, so use Lax/Strict for localhost
    if (isLocalhost) {
      cookieString += ';SameSite=Lax';
    } else {
      cookieString += ';SameSite=None';
    }
    
    // Add expiry if provided
    if (expires) {
      cookieString += `;expires=${expires.toUTCString()}`;
    }
    
    // DO NOT set domain attribute - this scopes cookie to exact hostname for subdomain isolation
    
    document.cookie = cookieString;
    console.log(`🍪 Cookie set (localhost: ${isLocalhost}, scoped to: ${window.location.hostname}): ${cookieString.substring(0, 50)}...`);
  }, []);

  // Simple encryption/decryption utilities for refresh token storage
  // Note: This provides basic obfuscation. For production, consider more robust encryption.
  const encryptToken = (token) => {
    try {
      // Simple base64 encoding with a salt (better than plain text)
      const salt = 'ziro_refresh_salt_v1';
      const combined = salt + token;
      return btoa(combined);
    } catch (e) {
      console.error("Error encrypting token:", e);
      return token; // Fallback to plain token
    }
  };

  const decryptToken = (encrypted) => {
    try {
      const salt = 'ziro_refresh_salt_v1';
      const combined = atob(encrypted);
      return combined.substring(salt.length);
    } catch (e) {
      console.error("Error decrypting token:", e);
      return encrypted; // Fallback to returning as-is
    }
  };

  const decodeJwtResponse = (token) => {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  };

  const getHigherResProviderPhotoUrl = function (photoURL, providerId) {
    //workaround to get higer res profile picture
    let result = photoURL;
    if (providerId.includes("google")) {
      result = photoURL.replace("s96-c", "s400-c");
    } else if (providerId.includes("facebook")) {
      result = `${photoURL}?type=large`;
    }
    return result;
  };

  /**
   * Create session after successful login
   * This replaces the old token-based authentication with server-side sessions
   */
  const createSession = async (idToken, userId, profileImageUrl, userPayload) => {
    try {
      console.log("🔄 Creating session with server-side session management...");
      
      // Get refresh token from localStorage if available (optional)
      const refreshToken = localStorage.getItem('google_refresh_token') || null;
      const accessToken = localStorage.getItem('google_access_token') || null;
      
      // Prepare session creation request
      const sessionRequest = {
        id_token: idToken,
      };
      
      if (refreshToken) {
        sessionRequest.refresh_token = refreshToken;
      }
      if (accessToken) {
        sessionRequest.access_token = accessToken;
      }
      
      // Call /createsession endpoint
      const response = await fetch(serverUrl + "/createsession", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sessionRequest)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Session creation failed:", response.status, errorText);
        // Fallback: continue with token-based auth if session creation fails
        setAuthCookie(idToken);
        handleLoginSuccess(userId, profileImageUrl, userPayload, null);
        return;
      }
      
      const sessionData = await response.json();
      console.log("✅ Session created successfully:", sessionData);
      
      // Store session_id instead of id_token
      const sessionId = sessionData.session_id;
      
      // Calculate cookie expiration date (expires_at is in seconds, convert to Date)
      const cookieExpires = new Date(sessionData.expires_at * 1000);
      setAuthCookie(sessionId, cookieExpires); // Store session_id in cookie with proper expiration
      
      // Also store in localStorage as backup (persists even if cookie expires)
      localStorage.setItem('ziro_session_id', sessionId);
      localStorage.setItem('session_expires_at', sessionData.expires_at.toString());
      
      console.log(`🍪 Session cookie expires at: ${cookieExpires.toLocaleString()}`);
      console.log(`💾 Session stored in localStorage as backup`);
      
      // Store id_token in localStorage for reference (may be needed for some legacy flows)
      localStorage.setItem('google_id_token', idToken);
      
      // Clear old token refresh scheduling (no longer needed with server-side sessions)
      if (timer) {
        clearTimeout(timer);
        setTimer(null);
      }
      
      console.log("✅ Session stored, proceeding with login success...");
      handleLoginSuccess(userId, profileImageUrl, userPayload, sessionData);
      
    } catch (error) {
      console.error("❌ Error creating session:", error);
      // Fallback: continue with token-based auth if session creation fails
      setAuthCookie(idToken);
      handleLoginSuccess(userId, profileImageUrl, userPayload, null);
    }
  };
  
  /**
   * Handle successful login (called after profile creation and optionally session creation)
   */
  const handleLoginSuccess = (userId, profileImageUrl, userPayload, sessionData) => {
    // Dispatch Redux actions
    console.log("🔍 DEBUG: About to dispatch LOGIN action");
    props.dispatch({ type: "LOGIN" });
    console.log("🔍 DEBUG: About to dispatch USERID action with id:", userId);
    props.dispatch({ type: "USERID", id: userId });
    console.log("🔍 DEBUG: Redux actions dispatched");
    
    // Ensure profileImageUrl is a string
    let imageUrl = profileImageUrl;
    if (imageUrl && typeof imageUrl !== 'string') {
      if (typeof imageUrl === 'object' && imageUrl.url) {
        imageUrl = imageUrl.url;
      } else if (typeof imageUrl === 'object' && imageUrl.toString) {
        imageUrl = String(imageUrl);
      } else {
        imageUrl = null;
      }
    }
    
    // Store user info (only if we have a valid string URL)
    if (imageUrl && typeof imageUrl === 'string' && imageUrl.trim() !== '') {
      setUserURL(imageUrl);
      localStorage.setItem("profile_image_url", imageUrl);
      props.dispatch({ type: "USERIMG", url: imageUrl });
    }
    setLoggedIn(true);
    localStorage.setItem("loggedin", "true");
    
    if (sessionData) {
      console.log("✅ Using server-side session management");
      console.log("📋 Session expires at:", new Date(sessionData.expires_at * 1000).toLocaleString());
      // Session management is handled server-side, no client-side refresh needed
    } else {
      console.log("⚠️ Fallback: Using legacy token-based authentication");
      // Legacy behavior: schedule token refresh
      if (userPayload && userPayload.exp) {
        const tokenExpiresAt = userPayload.exp * 1000;
        localStorage.setItem("token_expires_at", tokenExpiresAt.toString());
        scheduleTokenRefresh(tokenExpiresAt);
      }
    }
  };
  
  // Helper function to handle token sign-in (can be called from both credential and code flows)
  const handleTokenSignIn = async (credential, tokenPayload) => {
    console.log("🔍 Processing token sign-in...");
    console.log("🔍 User info:", tokenPayload);
    
    // Extract token expiry time (exp is in seconds, convert to milliseconds)
    const tokenExpiresAt = tokenPayload.exp * 1000;
    
    // Store user_id and id_token in localStorage for future use
    if (tokenPayload.sub) {
      localStorage.setItem("google_user_id", tokenPayload.sub);
      console.log("✅ Stored user_id in localStorage:", tokenPayload.sub);
    }
    
    // Store the id_token so we can extend its cookie expiry during refresh
    localStorage.setItem("google_id_token", credential);
    console.log("✅ Stored id_token in localStorage");
    
    // ID_TOKEN DEBUG_LOG_START
    if (DEBUG_COOKIE_RENEWAL) {
      console.log("\n🔐═══════════════════════════════════════");
      console.log("LOGIN: Initial authentication completed");
      console.log("═══════════════════════════════════════");
      debugDecodeJwt(credential, "Initial Login JWT");
      console.log("═══════════════════════════════════════\n");
    }
    // ID_TOKEN DEBUG_LOG_END
    
    // Make backend request
    var xhr = new XMLHttpRequest();
    console.log("🔍 Making tokensignin request to:", serverUrl + "/tokensignin");
    xhr.open("POST", serverUrl + "/tokensignin");
    xhr.setRequestHeader("Content-Type", "application/x-www-form-urlencoded");
    var highResimageUrl = getHigherResProviderPhotoUrl(
      tokenPayload.picture,
      "google"
    );
    
    xhr.onload = function () {
      console.log("🔍 tokensignin response, status:", xhr.status);
      if (xhr.status === 200) {
        console.log("✅ tokensignin successful:", xhr.responseText);
        const userId = xhr.responseText;
        
        // After successful profile creation, create session
        createSession(credential, userId, highResimageUrl, tokenPayload);
      } else {
        console.error("❌ tokensignin failed:", xhr.status, xhr.responseText);
      }
    };
    xhr.onerror = function () {
      console.error("❌ tokensignin network error");
    };

    xhr.send(
      "idtoken=" +
        credential +
        "&imgURL=" +
        highResimageUrl +
        "&fname=" +
        tokenPayload.given_name +
        "&lname=" +
        tokenPayload.family_name
    );
  };
  
  // DEPRECATED: Old credential button flow - no longer used
  // TODO: Remove this function in future cleanup
  const handleCredentialResponse = (response) => {
    console.log("🔍 DEBUG: GIS response received:", response);
    console.log("🔍 DEBUG: Client ID being used:", globalClientId);
    
    // Decode the JWT token to get user info
    const responsePayload = decodeJwtResponse(response.credential);
    console.log("User info:", responsePayload);
    
    // Extract token expiry time (exp is in seconds, convert to milliseconds)
    const tokenExpiresAt = responsePayload.exp * 1000;
    
    // Set user object with GIS response format
    const userResponse = {
      tokenId: response.credential,
      expiresAt: tokenExpiresAt,
      profileObj: {
        name: responsePayload.name,
        email: responsePayload.email,
        imageUrl: responsePayload.picture,
        givenName: responsePayload.given_name,
        familyName: responsePayload.family_name
      }
    };
    
    setUserObj(userResponse);
    
    // Clear and set cookie
    document.cookie = "ziroatoken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    setAuthCookie(response.credential);
    
    // ID_TOKEN DEBUG_LOG_START
    if (DEBUG_COOKIE_RENEWAL) {
      console.log("\n🍪═══════════════════════════════════════");
      console.log("COOKIE SET: Initial login cookie");
      console.log("═══════════════════════════════════════");
      console.log("⚠️ NOTE: No explicit expiry set - will be session cookie!");
      debugShowCurrentCookie();
      console.log("═══════════════════════════════════════\n");
    }
    // ID_TOKEN DEBUG_LOG_END
    
    // Make backend request to create profile (tokensignin)
    var xhr = new XMLHttpRequest();
    console.log("🔍 DEBUG: Making request to:", serverUrl + "/tokensignin");
    xhr.open("POST", serverUrl + "/tokensignin");
    xhr.setRequestHeader("Content-Type", "application/x-www-form-urlencoded");
    
    var highResimageUrl = getHigherResProviderPhotoUrl(
      responsePayload.picture,
      "google"
    );
    
    xhr.onload = function () {
      console.log("🔍 DEBUG: xhr.onload function called, status:", xhr.status);
      if (xhr.status === 200) {
        console.log("✅ tokensignin successful:", xhr.responseText);
        const userId = xhr.responseText;
        
        // After successful profile creation, create session
        createSession(response.credential, userId, highResimageUrl, responsePayload);
      } else {
        console.error("❌ tokensignin failed:", xhr.status, xhr.responseText);
      }
    };
    xhr.onerror = function () {
      console.error("❌ tokensignin network error");
    };

    xhr.send(
      "idtoken=" +
        response.credential +
        "&imgURL=" +
        highResimageUrl +
        "&fname=" +
        responsePayload.given_name +
        "&lname=" +
        responsePayload.family_name
    );
    
    //window.location.reload();
  };

  // Handle user clicking "Enable Auto-Renewal" button
  const handleEnableAutoRenewal = () => {
    console.log("🔘 User clicked 'Enable Auto-Renewal' button");
    
    if (!tokenClient) {
      console.error("❌ Token client not initialized yet");
      return;
    }
    
    setIsRequestingRefreshToken(true);
    
    try {
      // This will open a popup for user consent (triggered by user click, so allowed!)
      console.log("🔄 Requesting authorization code via user-initiated action...");
      tokenClient.requestCode();
      console.log("✅ Authorization code request initiated (popup should appear)");
    } catch (error) {
      console.error("❌ Failed to request authorization code:", error);
      setIsRequestingRefreshToken(false);
    }
  };
  
  // Handle user dismissing the auto-renewal banner
  const handleDismissAutoRenewBanner = () => {
    console.log("❌ User dismissed auto-renewal banner");
    setShowAutoRenewBanner(false);
    // Store dismissal in localStorage so we don't show it again this session
    localStorage.setItem('auto_renew_banner_dismissed', 'true');
  };

  // Token refresh logic - schedules automatic renewal before expiry
  // MOVED BEFORE requestNewToken to fix React 18 circular dependency
  const scheduleTokenRefresh = React.useCallback((expiresAt) => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[${timestamp}] 🔧 scheduleTokenRefresh called with expiresAt:`, expiresAt);
    
    // Calculate when to refresh: 5 minutes before expiry, or 5 minutes from now (whichever is later)
    const oneMin = 60 * 1000;
    const fiveMin = 5 * oneMin;
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;
    const refreshDeadline = Math.max(fiveMin, timeUntilExpiry - fiveMin);
    
    const minutesUntilRefresh = Math.floor(refreshDeadline / oneMin);
    console.log(`[${timestamp}] 🔄 Token will refresh in ${minutesUntilRefresh} minutes`);
    console.log(`[${timestamp}] ⏰ Token expires at: ${new Date(expiresAt).toLocaleString()}`);
    
    // Schedule the refresh - requestNewToken will be defined below
    const newTimer = setTimeout(() => {
      const refreshTime = new Date().toLocaleTimeString();
      console.log(`[${refreshTime}] 🔄 Initiating automatic token refresh...`);
      requestNewToken();
    }, refreshDeadline);
    
    // Clear any existing timer AFTER creating new one to avoid timing gaps
    setTimer(prevTimer => {
      if (prevTimer) {
        clearTimeout(prevTimer);
        console.log(`[${timestamp}] 🔧 Cleared previous timer`);
      }
      return newTimer;
    });
    
    console.log(`[${timestamp}] 🔧 Timer set, will trigger in ${minutesUntilRefresh} minutes`);
  }, []);

  // Request a new token using backend refresh token endpoint
  const requestNewToken = useCallback(async () => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[${timestamp}] 🔧 requestNewToken called`);
    
    // Note: Cross-tab cooldown check removed - not needed with server-side session management
    
    // ID_TOKEN DEBUG_LOG_START
    if (DEBUG_COOKIE_RENEWAL) {
      console.log("\n🔄═══════════════════════════════════════");
      console.log("REFRESH TRIGGERED: Requesting new token");
      console.log("═══════════════════════════════════════");
      console.log("⏰ Timestamp:", new Date().toLocaleString());
      console.log("\n📸 BEFORE REFRESH:");
      window.beforeRefreshJwt = debugShowCurrentCookie();
      console.log("═══════════════════════════════════════\n");
    }
    // ID_TOKEN DEBUG_LOG_END
    
    // Get refresh token from localStorage
    const encryptedRefreshToken = localStorage.getItem('google_refresh_token');
    
    if (!encryptedRefreshToken) {
      console.error("❌ No refresh token found, user needs to re-authenticate");
      // Clear session and prompt re-login
      localStorage.setItem("loggedin", "false");
      localStorage.removeItem("profile_image_url");
      localStorage.removeItem("token_expires_at");
      setLoggedIn(false);
      
      // Try to re-prompt the user
      if (window.google && window.google.accounts && window.google.accounts.id) {
        console.log("🔄 Prompting user for re-authentication...");
        window.google.accounts.id.prompt();
      }
      return;
    }
    
    // Decrypt refresh token
    const refreshTokenValue = decryptToken(encryptedRefreshToken);
    
    console.log("🔄 Requesting new access token from backend...");
    console.log("🔄 Refresh token (first 20 chars):", refreshTokenValue.substring(0, 20) + "...");
    
    try {
      // Get refresh token endpoint from service config with robust fallbacks
      let endpoint;
      if (window.getRefreshTokenEndpoint) {
        endpoint = window.getRefreshTokenEndpoint();
      }
      
      if (!endpoint || endpoint === 'undefined/refresh-google-token') {
        // Fallback to ZIRO_AUTH_SERVER
        // Use centralized config (should not need fallback if config_loader.js works)
        const authServer = window.getAuthUrl ? window.getAuthUrl() : (window.ZIRO_AUTH_SERVER || 'https://ziroaidev.appspot.com');
        endpoint = `${authServer}/refresh-google-token`;
        console.log("⚠️ Using fallback auth server:", authServer);
      }
      
      console.log("🔄 Calling endpoint:", endpoint);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh_token: refreshTokenValue
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error("❌ Token refresh failed:", response.status, errorData);
        
        // If refresh token is invalid or expired, clear session
        if (response.status === 401) {
          console.log("🔐 Refresh token invalid or expired. Clearing session...");
          localStorage.setItem("loggedin", "false");
          localStorage.removeItem("profile_image_url");
          localStorage.removeItem("token_expires_at");
          localStorage.removeItem("google_refresh_token");
          setLoggedIn(false);
          
          // Prompt user to re-authenticate
          if (window.google && window.google.accounts && window.google.accounts.id) {
            window.google.accounts.id.prompt();
          }
        }
        return;
      }
      
      const data = await response.json();
      console.log("✅ Token refreshed successfully from backend");
      console.log("🔄 New access token (first 20 chars):", data.access_token.substring(0, 20) + "...");
      console.log("🔄 Expires in:", data.expires_in, "seconds");
      
      // ID_TOKEN DEBUG_LOG_START
      if (DEBUG_COOKIE_RENEWAL) {
        console.log("\n📥═══════════════════════════════════════");
        console.log("BACKEND RESPONSE: /refresh-google-token");
        console.log("═══════════════════════════════════════");
        console.log("📦 Full response:", JSON.stringify(data, null, 2));
        console.log("🔑 Has access_token:", !!data.access_token);
        console.log("🔑 Has id_token:", !!data.id_token);
        console.log("⏰ expires_in:", data.expires_in, "seconds");
        
        if (data.id_token) {
          console.log("\n✨ NEW id_token received from backend:");
          debugDecodeJwt(data.id_token, "New ID Token from Backend");
        } else {
          console.log("\n⚠️ NO id_token in backend response (expected behavior)");
        }
        console.log("═══════════════════════════════════════\n");
      }
      // ID_TOKEN DEBUG_LOG_END
      
      // Update access token
      setAccessToken(data.access_token);
      
      // Calculate new expiry time
      const expiresIn = data.expires_in || 3600;
      const newExpiresAt = Date.now() + (expiresIn * 1000);
      
      // Store new expiry time
      localStorage.setItem("token_expires_at", newExpiresAt.toString());
      
      // CRITICAL FIX: We need a NEW id_token, not reuse the old expired one!
      // Google's refresh endpoint doesn't return id_token, so we need to request it from GIS
      // Strategy: After getting access_token, check if stored id_token is expired or near expiry
      // If so, trigger a silent credential request from Google Identity Services
      
      const storedIdToken = localStorage.getItem("google_id_token");
      let idTokenExpired = false;
      
      // Check if the stored id_token is expired or will expire soon
      // CRITICAL: Request new id_token proactively when access_token refreshes,
      // because id_tokens expire 1 hour after issue and access_token refresh happens at 55 minutes.
      // At refresh time, the id_token typically has only 5 minutes left.
      if (storedIdToken) {
        try {
          const tokenPayload = decodeJwtResponse(storedIdToken);
          const tokenExpiresAt = tokenPayload.exp * 1000; // Convert to milliseconds
          const now = Date.now();
          const timeUntilExpiry = tokenExpiresAt - now;
          
          // Request new id_token if:
          // 1. Token is already expired (timeUntilExpiry < 0)
          // 2. Token expires in less than 10 minutes (proactive refresh during access_token refresh)
          // This ensures we get a new id_token BEFORE the old one expires
          if (timeUntilExpiry < 10 * 60 * 1000) {
            idTokenExpired = true;
            if (timeUntilExpiry < 0) {
              console.log(`[${timestamp}] ❌ Stored id_token is EXPIRED (expired ${Math.floor(-timeUntilExpiry / 60000)} minutes ago) - URGENT: requesting new one`);
            } else {
              console.log(`[${timestamp}] ⚠️ Stored id_token expires in ${Math.floor(timeUntilExpiry / 60000)} minutes - requesting new one proactively`);
            }
          } else {
            console.log(`[${timestamp}] ✅ Stored id_token still valid (expires in ${Math.floor(timeUntilExpiry / 60000)} minutes)`);
          }
        } catch (e) {
          console.error(`[${timestamp}] ❌ Error decoding stored id_token:`, e);
          idTokenExpired = true;
        }
      } else {
        idTokenExpired = true;
        console.log(`[${timestamp}] ⚠️ No stored id_token found`);
      }
      
      // If id_token is expired, request a new one from Google Identity Services
      if (idTokenExpired && window.google && window.google.accounts && window.google.accounts.id) {
        console.log(`[${timestamp}] 🔄 Requesting new id_token from Google Identity Services (silent)...`);
        
        // Set a flag so we can detect when a new credential is received during refresh
        window._ziroRefreshInProgress = true;
        
        // Try to get a new credential silently using OneTap
        // Note: The existing GIS callback (handleCredentialResponse or handleTokenSignIn) will handle it
        // We'll check for the new credential after a short delay
        try {
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
              console.log(`[${timestamp}] ⚠️ Silent credential request failed - user may need to re-authenticate`);
              window._ziroRefreshInProgress = false;
              // Fall back to using old token (user will need to re-login when it expires)
              fallbackToOldToken();
            } else {
              // Prompt was shown, wait for credential callback
              console.log(`[${timestamp}] 📋 OneTap prompt displayed, waiting for credential...`);
              // The existing credential handler will process it
              // Check after 3 seconds if we got a new token
              setTimeout(() => {
                window._ziroRefreshInProgress = false;
                const updatedToken = localStorage.getItem("google_id_token");
                if (updatedToken && updatedToken !== storedIdToken) {
                  console.log(`[${timestamp}] ✅ New id_token detected after prompt`);
                  // New token was set by credential handler, cookie should be updated too
                  const tokenPayload = decodeJwtResponse(updatedToken);
                  const tokenExpiresAt = tokenPayload.exp * 1000;
                  
                  // Ensure cookie is updated with new token
                  const cookieExpiry = new Date(tokenExpiresAt);
                  setAuthCookie(updatedToken, cookieExpiry);
                  
                  // Note: No broadcast needed - with server-side sessions, token refresh is handled server-side
                  // This code path is only used as fallback if session creation fails
                  scheduleTokenRefresh(tokenExpiresAt);
                } else {
                  console.log(`[${timestamp}] ⚠️ No new id_token received after prompt`);
                  fallbackToOldToken();
                }
              }, 3000);
            }
          });
        } catch (error) {
          console.error(`[${timestamp}] ❌ Error requesting new id_token:`, error);
          window._ziroRefreshInProgress = false;
          fallbackToOldToken();
        }
      } else if (idTokenExpired) {
        console.warn(`[${timestamp}] ⚠️ Google Identity Services not available, cannot get new id_token`);
        fallbackToOldToken();
      } else {
        // id_token is still valid, just update cookie expiry
        const cookieExpiry = new Date(newExpiresAt);
        setAuthCookie(storedIdToken, cookieExpiry);
        console.log(`[${timestamp}] ✅ Cookie expiry extended for valid id_token`);
        console.log(`[${timestamp}] 🔄 New cookie expires at: ${cookieExpiry.toLocaleString()}`);
        
        // Note: No broadcast needed - with server-side sessions, token refresh is handled server-side
        // This code path is only used as fallback if session creation fails
        
        // Schedule next refresh
        scheduleTokenRefresh(newExpiresAt);
        console.log(`[${timestamp}] ✅ Token refresh cycle completed successfully`);
      }
      
      // Fallback function if we can't get a new id_token
      function fallbackToOldToken() {
        const fallbackTime = new Date().toLocaleTimeString();
        const storedIdToken = localStorage.getItem("google_id_token");
        
        if (!storedIdToken) {
          console.error(`[${fallbackTime}] ❌ No stored id_token found - user needs to re-authenticate`);
          triggerReauthentication();
          return;
        }
        
        // CRITICAL: Check if the stored token is expired before using it
        try {
          const tokenPayload = decodeJwtResponse(storedIdToken);
          const tokenExp = tokenPayload.exp * 1000; // Convert to milliseconds
          const now = Date.now();
          const timeUntilExpiry = tokenExp - now;
          
          if (tokenExp < now) {
            // Token is expired - DON'T update cookie, trigger re-authentication
            console.error(`[${fallbackTime}] ❌ Stored id_token is EXPIRED (expired ${Math.floor(-timeUntilExpiry / 60000)} minutes ago)`);
            console.error(`[${fallbackTime}] ❌ NOT updating cookie with expired token`);
            console.error(`[${fallbackTime}] ❌ User needs to re-authenticate`);
            triggerReauthentication();
            return;
          } else {
            // Token is still valid (just couldn't get a new one) - use it but with shorter expiry
            console.log(`[${fallbackTime}] ⚠️ Using fallback: old id_token (still valid for ${Math.floor(timeUntilExpiry / 60000)} minutes)`);
            const cookieExpiry = new Date(Math.min(newExpiresAt, tokenExp)); // Use token's actual expiry, not access_token expiry
            setAuthCookie(storedIdToken, cookieExpiry);
            console.log(`[${fallbackTime}] ✅ Cookie updated with valid (but not renewed) id_token`);
            console.log(`[${fallbackTime}] 🔄 Cookie expires at: ${cookieExpiry.toLocaleString()}`);
            
            // Schedule next refresh based on token's actual expiry
            scheduleTokenRefresh(tokenExp);
            
            // Note: No broadcast needed - with server-side sessions, token refresh is handled server-side
            // This code path is only used as fallback if session creation fails
          }
        } catch (e) {
          console.error(`[${fallbackTime}] ❌ Error decoding stored id_token:`, e);
          console.error(`[${fallbackTime}] ❌ User needs to re-authenticate`);
          triggerReauthentication();
          return;
        }
        
      }
      
      // Helper function to trigger re-authentication when token refresh fails
      function triggerReauthentication() {
        const reauthTime = new Date().toLocaleTimeString();
        console.log(`[${reauthTime}] 🔐 Triggering re-authentication...`);
        
        // Clear session data
        localStorage.setItem("loggedin", "false");
        localStorage.removeItem("profile_image_url");
        localStorage.removeItem("token_expires_at");
        setLoggedIn(false);
        
        // Clear cookie (scoped to current subdomain only - no domain attribute)
        if (window.ZiroCrossTabAuthSync) {
          window.ZiroCrossTabAuthSync.clearAuthCookie();
        } else {
          // Fallback: manual cookie clear (scoped to current subdomain only)
          document.cookie = "ziroatoken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";
        }
        
        // Note: No broadcast needed - with server-side sessions, logout is handled server-side
        // This code path is only used as fallback if session creation fails
        
        // Clear any scheduled refresh
        if (timer) {
          clearTimeout(timer);
          setTimer(null);
        }
        
        // Try to prompt re-authentication
        if (window.google && window.google.accounts && window.google.accounts.id) {
          console.log(`[${reauthTime}] 🔐 Prompting Google re-authentication...`);
          try {
            window.google.accounts.id.prompt((notification) => {
              if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                console.log(`[${reauthTime}] ⚠️ Re-authentication prompt not displayed, user may need to refresh page`);
              }
            });
          } catch (error) {
            console.error(`[${reauthTime}] ❌ Error prompting re-authentication:`, error);
          }
        } else {
          console.warn(`[${reauthTime}] ⚠️ Google Identity Services not available for re-authentication`);
        }
        
        console.log(`[${reauthTime}] 🔐 Re-authentication triggered - user should see login prompt`);
      }
      
    } catch (error) {
      console.error("❌ Network error during token refresh:", error);
      // On network error, we might want to retry after a delay
      console.log("⏳ Will retry token refresh in 1 minute...");
      setTimeout(requestNewToken, 60000); // Retry after 1 minute
    }
  }, [decryptToken, scheduleTokenRefresh]);

  // Handle OAuth2 token response
  // DEPRECATED: Old token response handler - no longer used  
  // TODO: Remove this function in future cleanup
  const handleTokenResponse = (response) => {
    if (response.error) {
      console.error("❌ Token refresh failed:", response.error);
      // Token refresh failed, user needs to re-authenticate
      console.log("🔐 Token refresh failed. Please sign in again.");
      // Optionally trigger re-authentication
      if (window.google && window.google.accounts && window.google.accounts.id) {
        window.google.accounts.id.prompt();
      }
      return;
    }
    
    console.log("✅ Token refreshed successfully");
    setAccessToken(response.access_token);
    
    // Calculate new expiry time (tokens typically last 1 hour)
    const expiresIn = response.expires_in || 3600; // Default to 1 hour
    const newExpiresAt = Date.now() + (expiresIn * 1000);
    
    // Store new expiry time
    localStorage.setItem("token_expires_at", newExpiresAt.toString());
    
    // Schedule next refresh
    scheduleTokenRefresh(newExpiresAt);
    
    // NOTE: Don't update the cookie during token refresh!
    // The cookie contains the id_token which is still valid for authentication.
    // We only refresh the access_token (stored in memory) for Google API calls.
    console.log("✅ Cookie left unchanged during token refresh (id_token still valid)");
  };
  
  useEffect(() => {
    if (userObj) {
      console.log("User object updated:", userObj);
      
      // If user object has expiry, schedule refresh
      if (userObj.expiresAt) {
        scheduleTokenRefresh(userObj.expiresAt);
      }
    }
  }, [userObj]);





  const logout = async () => {
    console.log("🔓 Logging out user...");
    
    // Helper function to get cookie value
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    };
    
    // Get session_id before clearing everything
    const sessionId = getCookie("ziroatoken") || localStorage.getItem('ziro_session_id');
    
    // Step 0: Broadcast logout FIRST (6.5.4 fix) so other tabs receive it before we reload
    if (window.ZiroCrossTabAuthSync && window.ZiroCrossTabAuthSync.broadcastLogout) {
      try {
        if (!window.ZiroCrossTabAuthSync.channel) window.ZiroCrossTabAuthSync.initialize();
        window.ZiroCrossTabAuthSync.broadcastLogout({ redirectToLogin: false });
        console.log("📡 Logout broadcasted to other windows/tabs");
      } catch (e) {
        console.warn("⚠️ broadcastLogout failed:", e);
      }
    }
    
    // Step 1: Delete session from backend (Firestore)
    if (sessionId) {
      try {
        console.log("🗑️ Deleting session from backend...");
        const response = await fetch(serverUrl + "/deletesession", {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ session_id: sessionId })
        });
        
        if (response.ok) {
          console.log("✅ Session deleted from backend");
        } else {
          console.warn("⚠️ Failed to delete session from backend (continuing with logout)");
        }
      } catch (error) {
        console.error("❌ Error deleting session from backend:", error);
        // Continue with logout even if backend deletion fails
      }
    }
    
    // Step 2: Sign out from Google Identity Services
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.disableAutoSelect();
    }
    
    // Step 3: Revoke access token if available
    if (accessToken && window.google && window.google.accounts && window.google.accounts.oauth2) {
      window.google.accounts.oauth2.revoke(accessToken, () => {
        console.log("✅ Access token revoked");
      });
    }
    
    // Step 4: Clear all authentication data from localStorage
    localStorage.setItem("loggedin", "false");
    localStorage.removeItem("profile_image_url");
    localStorage.removeItem("token_expires_at");
    localStorage.removeItem("google_refresh_token");
    localStorage.removeItem("ziro_session_id"); // Clear session_id backup
    localStorage.removeItem("session_expires_at"); // Clear session expiry info
    
    // Step 5: Set a flag to prevent session restoration on next page load
    // This prevents race condition where restoration code runs before cookie is fully cleared
    sessionStorage.setItem("logout_in_progress", "true");
    
    // Step 6: Clear cookie (scoped to current subdomain only - no domain attribute)
    // Cookies are scoped to exact hostname (dev.ziro.io, tvd.ziro.io, ziro.io) for independent sessions
    document.cookie = "ziroatoken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";
    
    // Step 7: Clear React state
    setLoggedIn(false);
    setAccessToken(null);
    
    // Step 8: Dispatch LOGOUT action to Redux store
    props.dispatch({ type: "LOGOUT" });
    
    // Step 9: Clear timer
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
    
    // Step 10: Redirect if on user-specific pages
    if(history.location.pathname === "/user-view" && history.location.search === "" || history.location.pathname === "/user-profile"){
      history.push("/explore");  
    } 
    
    // Short delay (6.5.4 fix) so BroadcastChannel message is delivered to other tabs before reload
    console.log("✅ Logout complete, reloading page...");
    setTimeout(function() { window.location.reload(); }, 150);
  };

  // Check for existing authentication state on component mount
  // This restores sessions across browser restarts by validating with backend
  useEffect(() => {
    const checkAuthState = async () => {
      // Check if we just logged out (prevent session restoration after logout)
      if (sessionStorage.getItem("logout_in_progress") === "true") {
        console.log("🔒 Logout in progress, skipping session restoration");
        sessionStorage.removeItem("logout_in_progress"); // Clear the flag
        setLoggedIn(false);
        setUserURL(null);
        if (props.auth === 1) {
          props.dispatch({ type: "LOGOUT" });
        }
        return; // Don't try to restore session after logout
      }
      
      // Helper function to get cookie value
      const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return '';
      };
      
      const authToken = getCookie("ziroatoken"); // Check for actual token cookie
      
      // If cookie is empty, check localStorage for session_id as backup
      let sessionId = authToken;
      if (!sessionId) {
        sessionId = localStorage.getItem('ziro_session_id');
        if (sessionId) {
          console.log("🔍 Cookie empty, checking localStorage for session_id (browser restart scenario)");
        }
      }
      
      // Check if we have a valid session_id (UUID format) - indicates server-side session management
      const isSessionId = sessionId && sessionId.includes('-') && sessionId.length === 36 && !sessionId.includes('.');
      
      if (sessionId && isSessionId) {
        // We have a session_id - validate it with backend to restore session after browser restart
        console.log("🔄 Found session_id, validating with backend to restore session...");
        
        try {
          // Validate session by fetching profile (this also validates the session)
          const profileResponse = await fetch(`${serverUrl}/fetchprofile?session_id=${sessionId}`);
          
          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            
            if (profileData && profileData.length > 0) {
              const profile = profileData[0];
              const userId = profile.user_id || profile.id;
              
              // Get profile image URL and ensure it's a string
              // Note: get_profile_pic() may return the entire entity object, so we need to extract img_url carefully
              let profileImageUrl = null;
              
              // Try to get img_url from profile
              if (profile.img_url) {
                profileImageUrl = profile.img_url;
              } else if (profile.image_url) {
                profileImageUrl = profile.image_url;
              } else {
                profileImageUrl = localStorage.getItem("profile_image_url");
              }
              
              // Ensure profileImageUrl is a string (handle case where backend returns entire entity)
              if (profileImageUrl && typeof profileImageUrl !== 'string') {
                console.warn("⚠️ Profile image URL is not a string, extracting from object:", typeof profileImageUrl);
                // Backend bug: get_profile_pic() returns entire entity object instead of just URL
                // Extract img_url property from the entity object
                if (typeof profileImageUrl === 'object' && profileImageUrl.img_url) {
                  profileImageUrl = profileImageUrl.img_url;
                } else if (typeof profileImageUrl === 'object' && profileImageUrl.image_url) {
                  profileImageUrl = profileImageUrl.image_url;
                } else if (typeof profileImageUrl === 'object' && profileImageUrl.url) {
                  profileImageUrl = profileImageUrl.url;
                } else {
                  // If we can't extract a URL, try localStorage fallback or set to null
                  const fallbackUrl = localStorage.getItem("profile_image_url");
                  profileImageUrl = (fallbackUrl && typeof fallbackUrl === 'string') ? fallbackUrl : null;
                }
              }
              
              // Restore full authentication state
              console.log("✅ Session validated with backend - restoring authentication state");
        setLoggedIn(true);
              
              // Store user info
              if (userId) {
                localStorage.setItem("google_user_id", userId);
                localStorage.setItem("loggedin", "true");
                props.dispatch({ type: "USERID", id: userId });
              }
              
              // Restore profile image (only if it's a valid string)
              if (profileImageUrl && typeof profileImageUrl === 'string' && profileImageUrl.trim() !== '') {
        setUserURL(profileImageUrl);
                localStorage.setItem("profile_image_url", profileImageUrl);
        props.dispatch({ type: "USERIMG", url: profileImageUrl });
              }
              
              // Restore Redux login state
        props.dispatch({ type: "LOGIN" });
        
              // Ensure cookie is set with proper expiration (in case cookie was cleared but localStorage wasn't)
        const sessionExpiresAt = localStorage.getItem('session_expires_at');
              if (sessionExpiresAt && !authToken) {
                // Cookie was missing but localStorage has session - restore cookie
                const expiresAt = parseInt(sessionExpiresAt, 10);
                const cookieExpires = new Date(expiresAt * 1000);
                setAuthCookie(sessionId, cookieExpires);
                console.log("🍪 Restored session cookie from localStorage");
              }
              
              // Check session expiry info
          if (sessionExpiresAt) {
            const expiresAt = parseInt(sessionExpiresAt, 10);
            const now = Date.now();
            const minutesRemaining = Math.floor((expiresAt * 1000 - now) / 60000);
                if (minutesRemaining > 0) {
                  console.log(`📋 Session restored - expires in ${minutesRemaining} minutes (managed server-side)`);
                } else {
                  console.log("⚠️ Session expired according to localStorage, but backend validated it - session may have been extended");
                }
              }
              
              console.log("✅ Session restored successfully after browser restart");
              return; // Successfully restored, exit
            }
          } else if (profileResponse.status === 401) {
            // Session invalid or expired
            console.log("❌ Session invalid or expired - clearing local data");
            localStorage.setItem("loggedin", "false");
            localStorage.removeItem("ziro_session_id");
            localStorage.removeItem("session_expires_at");
            setLoggedIn(false);
            setUserURL(null);
            if (props.auth === 1) {
              props.dispatch({ type: "LOGOUT" });
            }
            return;
          }
        } catch (error) {
          console.error("❌ Error validating session with backend:", error);
          // If validation fails, don't restore - user will need to log in again
        }
      }
      
      // Fallback: Check for legacy token-based auth or if we have cached auth state
      const isLoggedIn = localStorage.getItem("loggedin") === "true";
      let profileImageUrl = localStorage.getItem("profile_image_url");
      const tokenExpiresAt = localStorage.getItem("token_expires_at");
      const hasReduxAuth = props.auth === 1;
      
      // Ensure profileImageUrl is a string
      if (profileImageUrl && typeof profileImageUrl !== 'string') {
        if (typeof profileImageUrl === 'object' && profileImageUrl.url) {
          profileImageUrl = profileImageUrl.url;
        } else {
          profileImageUrl = null;
        }
      }
      
      // Only restore from cache if we have all indicators and Redux is already set
      if (isLoggedIn && profileImageUrl && typeof profileImageUrl === 'string' && profileImageUrl.trim() !== '' && authToken && hasReduxAuth) {
        console.log("✅ Restoring from cached auth state (Redux already initialized)");
        setLoggedIn(true);
        setUserURL(profileImageUrl);
        props.dispatch({ type: "USERIMG", url: profileImageUrl });
        props.dispatch({ type: "LOGIN" });
        
        // Check session expiry for legacy tokens
        if (tokenExpiresAt && !isSessionId) {
          const expiresAt = parseInt(tokenExpiresAt, 10);
          const now = Date.now();
          
          if (expiresAt > now) {
            console.log("🔄 Existing legacy token found, scheduling token refresh");
            scheduleTokenRefresh(expiresAt);
          } else {
            console.log("⚠️ Legacy token expired, attempting to refresh with refresh token...");
            const encryptedRefreshToken = localStorage.getItem('google_refresh_token');
            if (encryptedRefreshToken) {
              console.log("✅ Refresh token found, requesting new access token");
              requestNewToken();
            } else {
              console.log("❌ No refresh token available, user needs to re-authenticate");
              setLoggedIn(false);
              localStorage.setItem("loggedin", "false");
              localStorage.removeItem("profile_image_url");
              localStorage.removeItem("token_expires_at");
            }
          }
        }
      } else {
        // No valid authentication - clear state
        console.log("🔒 No valid authentication found. isLoggedIn:", isLoggedIn, "profileImageUrl:", !!profileImageUrl, "authToken:", !!authToken, "hasReduxAuth:", hasReduxAuth);
        setLoggedIn(false);
        setUserURL(null);
        // Clear stale localStorage data and Redux state
        if (isLoggedIn || profileImageUrl || props.auth === 1) {
          console.log("⚠️ Clearing stale localStorage data and Redux auth state");
          localStorage.setItem("loggedin", "false");
          if (props.auth === 1) {
            props.dispatch({ type: "LOGOUT" });
          }
        }
      }
    };
    
    checkAuthState();
  }, [props.auth]);

  // Sync Redux imgURL to local state (fixes avatar not showing after login)
  // FIXED: Less aggressive - keep avatar if we have any auth indicators, even if loggedIn state is temporarily false
  useEffect(() => {
    // Helper to check if user has valid token cookie
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return '';
    };
    
    const authToken = getCookie("ziroatoken");
    const hasLocalStorageFlag = localStorage.getItem("loggedin") === "true";
    const hasProfileImage = localStorage.getItem("profile_image_url");
    const hasReduxAuth = props.auth === 1;
    
    // More lenient: Check if we have ANY auth indicators (cookie, localStorage flag, or Redux auth)
    // This prevents clearing avatar during transient state issues (e.g., token refresh)
    const hasAnyAuthIndicator = authToken || hasLocalStorageFlag || hasReduxAuth;
    const isAuthenticated = loggedIn && authToken;
    
    console.log("🔍 Redux imgURL changed:", props.imgURL);
    console.log("🔍 Current userImgURL:", userImgURL);
    console.log("🔍 loggedIn state:", loggedIn);
    console.log("🔍 Has auth token:", !!authToken);
    console.log("🔍 Has localStorage flag:", hasLocalStorageFlag);
    console.log("🔍 Has any auth indicator:", hasAnyAuthIndicator);
    
    // If we have auth indicators, keep/show avatar even if loggedIn is temporarily false
    if (isAuthenticated || (hasAnyAuthIndicator && hasProfileImage)) {
      if (props.imgURL && typeof props.imgURL === 'string') {
        console.log("🖼️ Syncing avatar from Redux:", props.imgURL.substring(0, 50) + "...");
        setUserURL(props.imgURL);
      } else if (hasProfileImage) {
        // Fallback to localStorage - keep showing avatar if we have cached data
        const storedUrl = localStorage.getItem("profile_image_url");
        if (storedUrl && typeof storedUrl === 'string') {
          console.log("🖼️ Syncing avatar from localStorage:", storedUrl.substring(0, 50) + "...");
          setUserURL(storedUrl);
        }
      }
    } else {
      // Only clear avatar if we have NO auth indicators at all (user is definitely logged out)
      // This prevents clearing during transient state issues
      if (userImgURL && !authToken && !hasLocalStorageFlag && !hasReduxAuth) {
        console.log("🔒 No auth indicators found, clearing avatar");
        setUserURL(null);
      } else if (userImgURL && !hasAnyAuthIndicator) {
        console.log("🔒 All auth indicators cleared, clearing avatar");
        setUserURL(null);
      }
    }
  }, [props.imgURL, loggedIn, props.auth]);

  // Sync with Redux auth state AND cookie check - ensure loggedIn is correct
  // FIXED: More lenient - only log out if we have clear evidence user should be logged out
  // Also add periodic session validation to restore state after idle time
  useEffect(() => {
    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return '';
    };
    
    const validateSession = async () => {
      const authToken = getCookie("ziroatoken");
      const sessionId = authToken || localStorage.getItem('ziro_session_id');
      const isSessionId = sessionId && sessionId.includes('-') && sessionId.length === 36 && !sessionId.includes('.');
      
      // If we have a session_id, validate it with backend
      if (isSessionId) {
        try {
          // Use main backend (serverUrl) for fetchprofile - auth service does not expose it in 6.5.4
          const response = await fetch(`${serverUrl}/fetchprofile?session_id=${sessionId}`, {
            method: 'GET',
            credentials: 'include'
          });
          
          if (response.ok) {
            const data = await response.json();
            if (data && data[0]) {
              // Session is valid - restore loggedIn state if needed
              if (!loggedIn) {
                console.log("✅ Session validated - restoring loggedIn state after idle time");
                setLoggedIn(true);
                // Restore avatar if we have profile image
                if (data[0].photo_url || data[0].picture || data[0].profile_image_url) {
                  const profileImageUrl = data[0].photo_url || data[0].picture || data[0].profile_image_url;
                  localStorage.setItem("profile_image_url", profileImageUrl);
                  setUserURL(profileImageUrl);
                  props.dispatch({ type: "USERIMG", url: profileImageUrl });
                } else if (localStorage.getItem("profile_image_url")) {
                  const storedUrl = localStorage.getItem("profile_image_url");
                  setUserURL(storedUrl);
                }
              }
              return true;
            }
          } else if (response.status === 401) {
            // Session expired
            console.log("❌ Session expired during validation");
            return false;
          }
        } catch (error) {
          console.error("❌ Error validating session:", error);
          // Don't clear state on network errors - might be transient
          return null; // null means "unknown" - don't change state
        }
      }
      return false;
    };
    
    const authToken = getCookie("ziroatoken");
    const hasReduxAuth = props.auth === 1;
    const hasLocalStorageFlag = localStorage.getItem("loggedin") === "true";
    
    // More lenient check: If we have cookie OR localStorage flag, keep user logged in
    // Only log out if we have NO auth indicators
    const shouldBeLoggedIn = (hasReduxAuth && authToken) || (hasLocalStorageFlag && authToken);
    
    // Only update if there's a clear mismatch AND we have evidence
    if (loggedIn !== shouldBeLoggedIn) {
      // Only log out if we have NO auth indicators AND we're currently logged in
      if (!shouldBeLoggedIn && loggedIn && !authToken && !hasLocalStorageFlag) {
        // Before logging out, try to validate session (might be transient issue)
        validateSession().then((isValid) => {
          if (isValid === false) {
            // Session definitely invalid
            console.log("🔄 Syncing loggedIn state - logging out (no auth indicators found). Redux auth:", hasReduxAuth, "Has token:", !!authToken);
            setLoggedIn(false);
            // Don't clear avatar here - let the other useEffect handle it based on all indicators
          } else if (isValid === true) {
            // Session is valid - state will be restored by validateSession
            console.log("✅ Session validated - keeping user logged in");
          }
          // If isValid === null (network error), don't change state
        });
      } else if (shouldBeLoggedIn && !loggedIn) {
        // Log in if we have indicators
        console.log("🔄 Syncing loggedIn state - logging in. Redux auth:", hasReduxAuth, "Has token:", !!authToken);
        setLoggedIn(true);
      }
      // If we're logged in and still have token, don't change state (prevents premature logout)
    }
    
    // Periodic session validation (every 5 minutes) - run for any user with session/token so backend can extend session (fix: was !loggedIn, so never ran when already logged in)
    const validationInterval = setInterval(() => {
      const sessionId = getCookie("ziroatoken") || localStorage.getItem("ziro_session_id");
      const hasValidSessionId = sessionId && sessionId.includes("-") && sessionId.length === 36 && !sessionId.includes(".");
      const hasAuth = sessionId || localStorage.getItem("loggedin") === "true";
      if (hasValidSessionId || hasAuth) validateSession();
    }, 5 * 60 * 1000); // Every 5 minutes
    
    return () => clearInterval(validationInterval);
  }, [props.auth, loggedIn, props.dispatch]);

  // Initialize Google Identity Services and OAuth2 Token Client
  useEffect(() => {
    const initializeGIS = () => {
      if (window.google && window.google.accounts) {
        // Initialize OAuth2 Code Client (single popup for both login and refresh token)
        if (window.google.accounts.oauth2 && !tokenClient) {
          console.log("🔧 Initializing OAuth2 Token Client with offline access for refresh tokens");
          const client = window.google.accounts.oauth2.initCodeClient({
            client_id: globalClientId,
            scope: 'openid profile email',
            ux_mode: 'popup',
            access_type: 'offline',
            prompt: 'consent',
            callback: async (response) => {
              console.log("🔧 OAuth2 Code Client callback received");
              
              if (response.error) {
                console.error("❌ OAuth2 authorization error:", response.error);
                return;
              }
              
              // Google returns an authorization code
              console.log("✅ Received authorization code (will be used to get refresh token)");
              console.log("📝 Authorization code (first 20 chars):", response.code.substring(0, 20) + "...");
              
              // Send the authorization code to backend to exchange for refresh token
              try {
                // Construct the endpoint URL
                let endpoint;
                if (window.getRefreshTokenEndpoint && typeof window.getRefreshTokenEndpoint === 'function') {
                  const refreshEndpoint = window.getRefreshTokenEndpoint();
                  if (refreshEndpoint && refreshEndpoint !== 'undefined') {
                    endpoint = refreshEndpoint.replace('/refresh-google-token', '/exchange-auth-code');
                  }
                }
                
                // Fallback if endpoint is still undefined
                if (!endpoint) {
                  // Use centralized config (should not need fallback if config_loader.js works)
        const authServer = window.getAuthUrl ? window.getAuthUrl() : (window.ZIRO_AUTH_SERVER || 'https://ziroaidev.appspot.com');
                  endpoint = authServer + '/exchange-auth-code';
                  console.log("⚠️ Using fallback auth server:", authServer);
                }
                
                console.log("🔄 Sending authorization code to backend:", endpoint);
                
                const exchangeResponse = await fetch(endpoint, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    code: response.code
                  })
                });
                
                if (exchangeResponse.ok) {
                  const data = await exchangeResponse.json();
                  console.log("✅ Received tokens from backend");
                  
                  // Store the refresh token (encrypted)
                  if (data.refresh_token) {
                    const encrypted = encryptToken(data.refresh_token);
                    localStorage.setItem('google_refresh_token', encrypted);
                    console.log("✅ Refresh token stored successfully");
                    console.log("📝 Refresh token (first 20 chars):", data.refresh_token.substring(0, 20) + "...");
                  }
                  
                  // Update access token and expiry if provided
                  if (data.access_token && data.expires_in) {
                    const newExpiresAt = Date.now() + (data.expires_in * 1000);
                    localStorage.setItem("token_expires_at", newExpiresAt.toString());
                    console.log("✅ Access token updated, expires at:", new Date(newExpiresAt).toLocaleString());
                    
                    // Reschedule token refresh with new expiry
                    scheduleTokenRefresh(newExpiresAt);
                    
                    // Set cookie with the id_token (JWT) - this is what backend validateToken() expects
                    // Note: access_token is for Google APIs, id_token is for our authentication
                    if (data.id_token) {
                      setAuthCookie(data.id_token);
                      console.log("✅ Cookie updated with id_token");
                      try {
                        const base64Url = data.id_token.split('.')[1];
                        if (base64Url) {
                          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                          const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
                            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                          }).join(''));
                          const tokenPayload = JSON.parse(jsonPayload);
                          
                          console.log("🔍 ID Token payload:", tokenPayload);
                          
                          // Call tokensignin with the id_token (JWT format with user info)
                          await handleTokenSignIn(data.id_token, tokenPayload);
                        }
                      } catch (decodeError) {
                        console.error("❌ Failed to decode id_token:", decodeError);
                      }
                    } else {
                      console.error("❌ No id_token in response from backend");
                    }
                    
                    // Hide auto-renewal banner if it was showing
                    if (showAutoRenewBanner) {
                      setIsRequestingRefreshToken(false);
                      setAutoRenewSuccess(true);
                      console.log("🎉 Login and auto-renewal completed successfully!");
                      
                      // Auto-hide banner after 3 seconds
                      setTimeout(() => {
                        setShowAutoRenewBanner(false);
                        setAutoRenewSuccess(false);
                      }, 3000);
                    }
                  }
                } else {
                  const errorData = await exchangeResponse.json().catch(() => ({ error: 'Unknown error' }));
                  console.error("❌ Failed to exchange authorization code:", exchangeResponse.status, errorData);
                  setIsRequestingRefreshToken(false);
                }
              } catch (error) {
                console.error("❌ Error exchanging authorization code:", error);
                setIsRequestingRefreshToken(false);
              }
            },
          });
          setTokenClient(client);
          console.log("✅ OAuth2 Code Client initialized (supports offline access)");
        }
      } else {
        // Retry if Google Identity Services hasn't loaded yet
        setTimeout(initializeGIS, 100);
      }
    };
    
    initializeGIS();
  }, [loggedIn, tokenClient]);

  React.useEffect(() => {
    window.addEventListener("resize", updateColor);
    // Specify how to clean up after this effect:
    return function cleanup() {
      window.removeEventListener("resize", updateColor);
    };
  });
  // function that adds color white/transparent to the navbar on resize (this is for the collapse)
  const updateColor = () => {
    if (window.innerWidth < 993 && collapseOpen) {
      setcolor("bg-white");
    } else {
      setcolor("navbar-transparent");
    }
  };
  // this function opens and closes the collapse on small devices
  const toggleCollapse = () => {
    if (collapseOpen) {
      setcolor("navbar-transparent");
    } else {
      setcolor("bg-white");
    }
    setcollapseOpen(!collapseOpen);
  };
  // this function is to open the Search modal
  const toggleModalSearch = () => {
    setmodalSearch(!modalSearch);
  };
  return (
    <>
      {/* Auto-Renewal Banner - NO LONGER NEEDED (refresh token obtained on first login) */}
      {false && showAutoRenewBanner && loggedIn && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          maxWidth: '400px',
          backgroundColor: '#1e1e2e',
          border: '2px solid #27AE60',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <button
            onClick={handleDismissAutoRenewBanner}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: 'transparent',
              border: 'none',
              color: '#aaa',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0',
              width: '24px',
              height: '24px',
              lineHeight: '24px',
              textAlign: 'center'
            }}
            aria-label="Dismiss"
          >
            ×
          </button>
          
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '12px' }}>
            <span style={{ fontSize: '24px', marginRight: '12px' }}>
              {autoRenewSuccess ? '✅' : '🔄'}
            </span>
            <div>
              <h4 style={{ color: '#fff', margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                {autoRenewSuccess ? 'Auto-Renewal Enabled!' : 'Enable Auto-Renewal'}
              </h4>
              <p style={{ color: '#ccc', margin: '0', fontSize: '14px', lineHeight: '1.5' }}>
                {autoRenewSuccess 
                  ? 'Success! You will now stay logged in indefinitely. This banner will close automatically.'
                  : 'Stay logged in indefinitely! Click below to enable automatic token renewal and never get logged out again.'
                }
              </p>
            </div>
          </div>
          
          <button
            onClick={handleEnableAutoRenewal}
            disabled={isRequestingRefreshToken || autoRenewSuccess}
            style={{
              width: '100%',
              padding: '12px',
              backgroundColor: autoRenewSuccess ? '#27AE60' : (isRequestingRefreshToken ? '#555' : '#27AE60'),
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: (isRequestingRefreshToken || autoRenewSuccess) ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: (isRequestingRefreshToken || autoRenewSuccess) ? 0.8 : 1
            }}
            onMouseEnter={(e) => {
              if (!isRequestingRefreshToken && !autoRenewSuccess) {
                e.target.style.backgroundColor = '#229954';
              }
            }}
            onMouseLeave={(e) => {
              if (!isRequestingRefreshToken && !autoRenewSuccess) {
                e.target.style.backgroundColor = '#27AE60';
              }
            }}
          >
            {autoRenewSuccess ? '✅ Enabled Successfully!' : (isRequestingRefreshToken ? '⏳ Requesting Permission...' : '✓ Enable Auto-Renewal')}
          </button>
          
          {!autoRenewSuccess && (
            <p style={{ 
              color: '#888', 
              fontSize: '11px', 
              margin: '12px 0 0 0', 
              textAlign: 'center',
              lineHeight: '1.4'
            }}>
              {isRequestingRefreshToken 
                ? 'Please complete the authorization in the popup window. You can close it after granting permission.'
                : 'A popup will appear to request permission. This is a one-time action.'
              }
            </p>
          )}
        </div>
      )}
      
      <Navbar className={classNames("navbar-absolute", color)} expand="lg">
        <Container fluid>
          <div className="navbar-wrapper">
            <div
              className={classNames("navbar-toggle d-inline", {
                toggled: props.sidebarOpened,
              })}
            >
              <NavbarToggler onClick={props.toggleSidebar}>
                <span className="navbar-toggler-bar bar1" />
                <span className="navbar-toggler-bar bar2" />
                <span className="navbar-toggler-bar bar3" />
              </NavbarToggler>
            </div>
            <NavbarBrand href="/explore">
              {props.brandText}
            </NavbarBrand>
          </div>
          <NavbarToggler onClick={toggleCollapse}>
            <span className="navbar-toggler-bar navbar-kebab" />
            <span className="navbar-toggler-bar navbar-kebab" />
            <span className="navbar-toggler-bar navbar-kebab" />
          </NavbarToggler>
          <Collapse navbar isOpen={collapseOpen}>
            <Nav className="ml-auto" navbar>
              {loggedIn ? (
                <UncontrolledDropdown nav>
                  <DropdownToggle
                    caret
                    color="default"
                    nav
                    onClick={(e) => e.preventDefault()}
                  >
                    {userImgURL ? (
                      <div className="photo">
                        <img 
                          alt="User profile" 
                          src={userImgURL}
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            console.error('❌ Avatar image failed to load:', userImgURL);
                            console.error('❌ Error details:', e);
                            // Show placeholder icon instead of hiding
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<i class="tim-icons icon-single-02" style="font-size: 18px; display: flex; align-items: center; justify-content: center; height: 100%;"></i>';
                          }}
                          onLoad={() => {
                            console.log('✅ Avatar image loaded successfully:', userImgURL.substring(0, 50) + '...');
                          }}
                        />
                      </div>
                    ) : (
                      <div className="photo" style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#344675',
                        color: '#fff',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        <i className="tim-icons icon-single-02" style={{ fontSize: '18px' }}></i>
                      </div>
                    )}
                    <b className="caret d-none d-lg-block d-xl-block" />
                    <p className="d-lg-none">Account</p>
                  </DropdownToggle>
                  <DropdownMenu className="dropdown-navbar" right tag="ul">
                    <NavLink tag="li">
                      <Link to={{ pathname: "/user-profile" }}>
                        <DropdownItem className="nav-item" style={{color: "#3d3d3d"}}>
                          Profile
                        </DropdownItem>
                      </Link>
                    </NavLink>
                    <NavLink tag="li">
                      <Link to={{ pathname: "/settings" }}>
                        <DropdownItem className="nav-item" style={{color: "#3d3d3d"}}>
                          Settings
                        </DropdownItem>
                      </Link>
                    </NavLink>
                    <DropdownItem divider tag="li" />
                    <NavLink tag="li">
                      <DropdownItem className="nav-item">
                        <Button
                          color="link"
                          onClick={logout}
                          className="google-login-button-mine"
                        >
                          Logout
                        </Button>
                      </DropdownItem>
                    </NavLink>
                  </DropdownMenu>
                </UncontrolledDropdown>
              ) : (
                <UncontrolledDropdown nav>
                  <DropdownToggle
                    caret
                    color="default"
                    nav
                    onClick={(e) => e.preventDefault()}
                    title="Sign in to save projects and access full features"
                  >
                    <div className="photo" style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#FFC107',
                      color: '#1e1e2f',
                      fontSize: '18px',
                      fontWeight: 'bold'
                    }}>
                      <i className="fa fa-exclamation-triangle" style={{ fontSize: '16px' }}></i>
                    </div>
                    <b className="caret d-none d-lg-block d-xl-block" />
                    <p className="d-lg-none">Sign In</p>
                  </DropdownToggle>
                  <DropdownMenu className="dropdown-navbar" right tag="ul">
                    <NavLink tag="li">
                      <DropdownItem className="nav-item text-center" style={{padding: '15px'}}>
                        <p style={{color: '#3d3d3d', marginBottom: '10px'}}>
                          <i className="fa fa-info-circle"></i> Sign in to save projects
                        </p>
                        <button
                          onClick={() => {
                            if (tokenClient) {
                              console.log("🔘 User clicked 'Sign in with Google' from dropdown");
                              tokenClient.requestCode();
                            } else {
                              console.error("❌ Token client not initialized");
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 20px',
                            backgroundColor: '#ffffff',
                            border: '1px solid #d0d0d0',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
                            transition: 'all 0.2s',
                            outline: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <svg width="20" height="20" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                            <path fill="none" d="M0 0h48v48H0z"/>
                          </svg>
                          <span style={{ color: '#3c4043', fontWeight: '500' }}>Sign in with Google</span>
                        </button>
                      </DropdownItem>
                    </NavLink>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
              <li className="separator d-lg-none" />
            </Nav>
          </Collapse>
        </Container>
      </Navbar>
      <Modal
        modalClassName="modal-search"
        isOpen={modalSearch}
        toggle={toggleModalSearch}
      >
        <ModalHeader>
          <Input placeholder="SEARCH" type="text" />
          <button
            aria-label="Close"
            className="close"
            onClick={toggleModalSearch}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
        </ModalHeader>
      </Modal>
    </>
  );
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  imgURL: state.imgURL,
});
export default connect(mapStateToProps)(AdminNavbar);
